import React from "react";
import {
    Container,
    Row,
    Col,
    Button,
    Card,
    CardBody,
} from "reactstrap";

import { useNavigate } from "react-router-dom";

import {
    FiCheckCircle,
    FiArrowRight,
    FiBookOpen,
    FiBriefcase,
    FiUsers,
    FiAward,
    FiCpu,
    FiDatabase,
    FiBarChart2,
    FiSettings,
    FiCode,
    FiTool,
    FiHome,
} from "react-icons/fi";

import "./AICoursePage.css";
import SEO from "../../components/common/seo/SEO";


function AICoursePage() {

    const navigate = useNavigate();


    /* =========================
       PROGRAMMING / AI COURSES
    ========================= */

    const courses = [
        {
            title: "Python Courses",
            description:
                "Learn Python programming from fundamentals to practical application and build a strong foundation for AI and technology careers.",
            icon: <FiBookOpen />,
            path: "/python-courses-pay-after-learn",
        },
        {
            title: "Artificial Intelligence",
            description:
                "Learn AI concepts, tools and practical techniques through structured training and real-world projects.",
            icon: <FiCpu />,
            path: "/ai-courses-pay-after-learn",
        },
        {
            title: "Machine Learning",
            description:
                "Build practical machine learning skills including algorithms, model building, data preparation and projects.",
            icon: <FiBarChart2 />,
            path: "/machine-learning-courses-pay-after-learn",
        },
        {
            title: "Data Science",
            description:
                "Learn data analysis, visualization, statistics and machine learning techniques for data-driven careers.",
            icon: <FiDatabase />,
            path: "/data-science-courses-pay-after-learn",
        },
    ];


    /* =========================
       CAD AUTOMATION COURSES
    ========================= */

    const cadCourses = [
        {
            title: "NX Open Customization & Automation",
            description:
                "Learn NX Open customization and automation using .NET to develop powerful CAD automation solutions.",
            icon: <FiCpu />,
            path: "/nx-open-customization-automation-training",
        },
        {
            title: "CATIA CAA Customization & Automation",
            description:
                "Learn CATIA CAA customization and automation for developing advanced CAD applications and solutions.",
            icon: <FiSettings />,
            path: "/catia-caa-customization-automation-training",
        },
        {
            title: "Python CATIA Automation & Customization",
            description:
                "Learn Python-based CATIA automation and customization to automate repetitive CAD design workflows.",
            icon: <FiCode />,
            path: "/python-catia-automation-customization",
        },
        {
            title: "Creo Toolkit Customization",
            description:
                "Learn Creo Toolkit customization and automation for developing customized engineering applications.",
            icon: <FiTool />,
            path: "/creo-toolkit-customization-training",
        },
        {
            title: "SOLIDWORKS API Customization & Automation",
            description:
                "Learn SOLIDWORKS API programming and automation to create customized CAD engineering solutions.",
            icon: <FiSettings />,
            path: "/solidworks-api-customization-automation-training",
        },
        {
            title: "AutoCAD .NET API Customization",
            description:
                "Learn AutoCAD .NET API development to automate drawing workflows and build custom CAD applications.",
            icon: <FiCode />,
            path: "/autocad-api-customization-automation-online-courses",
        },
        {
            title: "Autodesk Revit API Customization",
            description:
                "Learn Revit API development and automation for customized BIM and engineering workflows.",
            icon: <FiHome />,
            path: "/autodesk-revit-api-customization-automation-course",
        },
        {
            title: "Autodesk Inventor API Customization",
            description:
                "Learn Inventor API programming and automation to develop customized mechanical design solutions.",
            icon: <FiSettings />,
            path: "/autodesk-inventor-api-customization-automation-course",
        },
        {
            title: "Teamcenter PLM Customization",
            description:
                "Learn Teamcenter customization using ITK, RAC, SOA and AWC technologies for PLM solutions.",
            icon: <FiDatabase />,
            path: "/teamcenter-plm-customization-training-itk-rac-soa-awc",
        },
        {
            title: "PTC Windchill PLM Customization",
            description:
                "Learn PTC Windchill customization and development for enterprise PLM and engineering workflows.",
            icon: <FiBriefcase />,
            path: "/ptc-windchill-plm-customization-course",
        },
    ];


    /* =========================
       NAVIGATION
    ========================= */

    const handleCourseNavigation = (path) => {
        navigate(path);
    };


    const scrollToCourses = () => {
        document
            .getElementById("course-list")
            ?.scrollIntoView({
                behavior: "smooth",
            });
    };


    return (

        <>

            <SEO
                title="Courses List | Pay After You Learn Courses | AI, Python, ML, Data Science & CAD | FeesWorth"

                description="Explore FeesWorth courses in Python, Artificial Intelligence, Machine Learning, Data Science and CAD & PLM Automation. Build practical skills through structured training, projects and industry-focused learning."

                keywords="pay after you learn courses, Python course, Artificial Intelligence courses, Machine Learning courses, Data Science courses, CAD automation courses, CAD customization training, CAD PLM courses, FeesWorth courses"

                canonical="https://feesworth.com/ai-courses-pay-after-learn"
            />

            <div className="ai-course-page">


                {/* =====================================================
                HERO SECTION
            ===================================================== */}

                <section className="ai-course-hero">

                    <Container>

                        <Row className="align-items-center">


                            {/* HERO CONTENT */}

                            <Col
                                lg="7"
                                className="mb-4 mb-lg-0"
                            >

                                <span className="ai-course-badge">
                                    PAY AFTER YOU LEARN
                                </span>


                                <h1 className="ai-course-title">

                                    Learn. Build. Grow.

                                    <span>
                                        {" "}Pay After You Learn.
                                    </span>

                                </h1>


                                <p className="ai-course-description">

                                    Build practical skills in Python,
                                    Artificial Intelligence, Machine Learning,
                                    Data Science and CAD Automation through
                                    industry-focused training.

                                </p>


                                <div className="ai-course-actions">

                                    <Button
                                        color="primary"
                                        className="ai-course-primary-btn"
                                        onClick={scrollToCourses}
                                    >

                                        Explore Courses

                                        <FiArrowRight />

                                    </Button>


                                    <Button
                                        outline
                                        color="light"
                                        className="ai-course-secondary-btn"
                                        onClick={() =>
                                            navigate(
                                                "/ai-courses-pay-after-learn"
                                            )
                                        }
                                    >

                                        AI Courses

                                    </Button>

                                </div>

                            </Col>


                            {/* HERO CARD */}

                            <Col lg="5">

                                <div className="ai-course-hero-card">


                                    <div className="ai-course-hero-icon">

                                        <FiAward />

                                    </div>


                                    <h3>
                                        Pay After You Learn
                                    </h3>


                                    <p>

                                        Focus on learning first and follow
                                        the applicable payment terms after
                                        completing your training.

                                    </p>


                                    <div className="ai-course-hero-points">


                                        <div>

                                            <FiCheckCircle />

                                            <span>
                                                Industry-focused training
                                            </span>

                                        </div>


                                        <div>

                                            <FiCheckCircle />

                                            <span>
                                                Practical projects
                                            </span>

                                        </div>


                                        <div>

                                            <FiCheckCircle />

                                            <span>
                                                Career-oriented learning
                                            </span>

                                        </div>


                                    </div>

                                </div>

                            </Col>

                        </Row>

                    </Container>

                </section>



                {/* =====================================================
                PROGRAMMING / AI COURSES
            ===================================================== */}

                <section
                    className="ai-course-list-section"
                    id="course-list"
                >

                    <Container>


                        <div className="ai-course-section-heading">

                            <span>
                                TECHNOLOGY COURSES
                            </span>


                            <h2>
                                Choose Your Career Path
                            </h2>


                            <p>

                                Choose from our career-focused programs
                                in programming, Artificial Intelligence,
                                Machine Learning and Data Science.

                            </p>

                        </div>


                        <Row className="g-4">

                            {courses.map((course, index) => (

                                <Col
                                    md="6"
                                    lg="3"
                                    key={index}
                                >

                                    <Card
                                        className="ai-course-card"
                                        onClick={() =>
                                            handleCourseNavigation(
                                                course.path
                                            )
                                        }
                                    >

                                        <CardBody>


                                            <div className="ai-course-card-icon">

                                                {course.icon}

                                            </div>


                                            <h3>
                                                {course.title}
                                            </h3>


                                            <p>
                                                {course.description}
                                            </p>


                                            <div className="ai-course-card-link">

                                                Explore Course

                                                <FiArrowRight />

                                            </div>


                                        </CardBody>

                                    </Card>

                                </Col>

                            ))}

                        </Row>

                    </Container>

                </section>



                {/* =====================================================
                WHY CHOOSE US
            ===================================================== */}

                <section className="ai-course-benefits">

                    <Container>


                        <div className="ai-course-section-heading">

                            <span>
                                WHY CHOOSE US
                            </span>


                            <h2>
                                Learn With a Career-Focused Approach
                            </h2>


                            <p>

                                Build practical skills through structured
                                learning, hands-on assignments and
                                real-world projects.

                            </p>

                        </div>


                        <Row className="g-4">


                            {/* STRUCTURED LEARNING */}

                            <Col md="6" lg="3">

                                <Card className="ai-course-benefit-card">

                                    <CardBody>

                                        <div className="ai-course-benefit-icon">

                                            <FiBookOpen />

                                        </div>


                                        <h4>
                                            Structured Learning
                                        </h4>


                                        <p>

                                            Learn concepts step by step
                                            from fundamentals to advanced
                                            topics.

                                        </p>

                                    </CardBody>

                                </Card>

                            </Col>


                            {/* PRACTICAL PROJECTS */}

                            <Col md="6" lg="3">

                                <Card className="ai-course-benefit-card">

                                    <CardBody>

                                        <div className="ai-course-benefit-icon">

                                            <FiBriefcase />

                                        </div>


                                        <h4>
                                            Practical Projects
                                        </h4>


                                        <p>

                                            Work on practical projects
                                            designed around real-world
                                            use cases.

                                        </p>

                                    </CardBody>

                                </Card>

                            </Col>


                            {/* EXPERT GUIDANCE */}

                            <Col md="6" lg="3">

                                <Card className="ai-course-benefit-card">

                                    <CardBody>

                                        <div className="ai-course-benefit-icon">

                                            <FiUsers />

                                        </div>


                                        <h4>
                                            Expert Guidance
                                        </h4>


                                        <p>

                                            Get guidance throughout your
                                            learning journey from
                                            experienced trainers.

                                        </p>

                                    </CardBody>

                                </Card>

                            </Col>


                            {/* CAREER FOCUSED */}

                            <Col md="6" lg="3">

                                <Card className="ai-course-benefit-card">

                                    <CardBody>

                                        <div className="ai-course-benefit-icon">

                                            <FiAward />

                                        </div>


                                        <h4>
                                            Career Focused
                                        </h4>


                                        <p>

                                            Develop skills that can help
                                            you prepare for technology
                                            career opportunities.

                                        </p>

                                    </CardBody>

                                </Card>

                            </Col>


                        </Row>

                    </Container>

                </section>



                {/* =====================================================
                CAD AUTOMATION COURSES
            ===================================================== */}

                <section className="cad-automation-courses-section">

                    <Container>


                        <div className="ai-course-section-heading">

                            <span>
                                CAD AUTOMATION & CUSTOMIZATION
                            </span>


                            <h2>
                                CAD & PLM Automation Courses
                            </h2>


                            <p>

                                Build specialized CAD automation and
                                customization skills using industry-leading
                                CAD and PLM platforms.

                            </p>

                        </div>


                        <Row className="g-4">

                            {cadCourses.map((course, index) => (

                                <Col
                                    md="6"
                                    lg="4"
                                    key={index}
                                >

                                    <Card
                                        className="ai-course-card"
                                        onClick={() =>
                                            handleCourseNavigation(
                                                course.path
                                            )
                                        }
                                    >

                                        <CardBody>


                                            <div className="ai-course-card-icon">

                                                {course.icon}

                                            </div>


                                            <h3>
                                                {course.title}
                                            </h3>


                                            <p>
                                                {course.description}
                                            </p>


                                            <div className="ai-course-card-link">

                                                Explore Course

                                                <FiArrowRight />

                                            </div>


                                        </CardBody>

                                    </Card>

                                </Col>

                            ))}

                        </Row>

                    </Container>

                </section>



                {/* =====================================================
                PAY AFTER LEARN
            ===================================================== */}

                <section className="ai-course-pay-section">

                    <Container>

                        <Row className="align-items-center">


                            <Col lg="7">

                                <span className="ai-course-small-title">
                                    PAY AFTER YOU LEARN
                                </span>


                                <h2>

                                    Start Learning Without Paying
                                    the Full Fee Upfront

                                </h2>


                                <p>

                                    Explore our technology and CAD-focused
                                    courses and build practical skills
                                    through structured training and
                                    hands-on learning.

                                </p>


                                <div className="ai-course-check-list">


                                    <div>

                                        <FiCheckCircle />

                                        <span>
                                            Learn with a structured curriculum
                                        </span>

                                    </div>


                                    <div>

                                        <FiCheckCircle />

                                        <span>
                                            Complete practical assignments
                                        </span>

                                    </div>


                                    <div>

                                        <FiCheckCircle />

                                        <span>
                                            Build project experience
                                        </span>

                                    </div>


                                    <div>

                                        <FiCheckCircle />

                                        <span>
                                            Prepare for career opportunities
                                        </span>

                                    </div>


                                </div>

                            </Col>


                            <Col lg="5">

                                <div className="ai-course-pay-card">


                                    <FiAward className="ai-course-pay-icon" />


                                    <h3>
                                        Start Your Learning Journey
                                    </h3>


                                    <p>

                                        Choose a course and begin building
                                        practical skills for your technology
                                        or engineering career.

                                    </p>


                                    <Button
                                        color="primary"
                                        className="w-100"
                                        onClick={scrollToCourses}
                                    >

                                        View Courses

                                        <FiArrowRight />

                                    </Button>


                                </div>

                            </Col>

                        </Row>

                    </Container>

                </section>



                {/* =====================================================
                CTA
            ===================================================== */}

                <section className="ai-course-cta">

                    <Container>

                        <div className="ai-course-cta-content">


                            <span>
                                READY TO START?
                            </span>


                            <h2>
                                Start Your Learning Journey
                            </h2>


                            <p>

                                Choose the course that matches your
                                interests and career goals.

                            </p>


                            <Button
                                color="light"
                                className="ai-course-cta-btn"
                                onClick={scrollToCourses}
                            >

                                Explore Courses

                                <FiArrowRight />

                            </Button>


                        </div>

                    </Container>

                </section>


            </div>
        </>

    );
}

export default AICoursePage;